import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import researchService from '../../services/researchService';

ModuleRegistry.registerModules([AllCommunityModule]);

const theme = themeQuartz.withParams({
  headerBackgroundColor: '#212529',
  headerTextColor: '#fff',
  headerFontSize: 12,
  fontSize: 12,
  rowHeight: 32,
  headerHeight: 36,
});

const NO_SCALE_DISPLAY_FIELDS = new Set([
  'eps', 'eps_basic', 'eps_diluted', 'basic_eps', 'diluted_eps',
  'earnings_per_share', 'basic_earnings_per_share', 'diluted_earnings_per_share',
  'eps_basic_reported', 'eps_diluted_reported',
  'gross_margin', 'net_margin', 'operating_margin', 'ebitda_margin',
  'debt_to_equity', 'current_ratio', 'roe', 'roa', 'return_on_equity', 'return_on_assets',
  'fiscal_year', 'fiscal_quarter', 'year', 'quarter',
]);

/**
 * Interactive earnings data grid using AG Grid Community.
 *
 * Features:
 *   - Sort, filter, resize, reorder columns
 *   - Inline cell editing (metric values only)
 *   - Saves edits back to the backend via PATCH
 */
export default function EarningsGrid({ companyId, rows, onRefresh }) {
  const gridRef = useRef(null);
  const [saveState, setSaveState] = useState({}); // { rowId: 'saving'|'saved'|'error' }
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(new Set()); // set of field names
  const [deleteConfirm, setDeleteConfirm] = useState(null); // periodEndDate pending confirmation
  const [displayUnits, setDisplayUnits] = useState('ones'); // ones | thousands | millions | billions
  const displayDivisorRef = useRef(1);
  const [normalizing, setNormalizing] = useState(false);
  const displayUnitsLabelRef = useRef('Ones');

  // Close picker when clicking outside
  useEffect(() => {
    if (!colPickerOpen) return;
    const handler = (e) => {
      if (!e.target.closest('.col-picker-dropdown')) setColPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [colPickerOpen]);

  // Flatten each row: fixed fields + one key per metric
  const { rowData, metricKeys, customMetricKeys } = useMemo(() => {
    // Separate scalar metric keys from nested-object keys (e.g. custom_metrics: {k: v})
    const scalarKeys = new Set();
    // nestedParent -> Set of sub-keys across all rows
    const nestedMap = {};

    // These are top-level row fields — never treat them as metric columns
    // (the LLM often echoes them inside the metrics dict, which would overwrite
    // the correctly-mapped top-level values with null for rows that don't repeat them).
    const RESERVED_ROW_FIELDS = new Set([
      'fiscal_year', 'fiscal_quarter', 'quarter', 'period_end_date',
      'currency', 'reporting_units', 'reporting_period',
    ]);

    (rows || []).forEach((r) => {
      Object.entries(r.metrics || {}).forEach(([k, v]) => {
        if (RESERVED_ROW_FIELDS.has(k)) return;
        if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
          nestedMap[k] = nestedMap[k] || new Set();
          Object.keys(v).forEach((sk) => nestedMap[k].add(sk));
        } else {
          scalarKeys.add(k);
        }
      });
    });

    const keys = Array.from(scalarKeys);
    // All flattened sub-keys from nested objects
    const flatCustomKeys = Object.values(nestedMap).flatMap((s) => Array.from(s));

    const data = (rows || []).map((r) => {
      const row = {
        _company_id: r.company_id,
        _period_end_date: r.period_end_date,
        fiscal_year: r.fiscal_year ?? '',
        quarter: r.quarter || '',
        period_end_date: r.period_end_date,
        currency: r.currency || 'USD',
        reporting_units: r.reporting_units || '',
        source_filename: r.source_filename || '',
      };
      // Scalar metrics
      keys.forEach((k) => { row[k] = r.metrics?.[k] ?? null; });
      // Flattened nested metrics (read-only; keyed as sub-key name)
      Object.entries(nestedMap).forEach(([parentKey, subKeys]) => {
        const nested = r.metrics?.[parentKey];
        const nested_obj = (nested !== null && typeof nested === 'object') ? nested : {};
        subKeys.forEach((sk) => { row[sk] = nested_obj[sk] ?? null; });
      });
      return row;
    });
    return { rowData: data, metricKeys: keys, customMetricKeys: flatCustomKeys };
  }, [rows]);

  const getRowId = useCallback((params) => params.data._period_end_date, []);

  const onCellValueChanged = useCallback(async (params) => {
    const { data } = params;
    const updatedMetrics = Object.fromEntries(metricKeys.map((k) => [k, data[k]]));
    const rowId = data._period_end_date;
    setSaveState((s) => ({ ...s, [rowId]: 'saving' }));
    try {
      await researchService.patchEarnings(data._company_id, data._period_end_date, updatedMetrics);
      setSaveState((s) => ({ ...s, [rowId]: 'saved' }));
      setTimeout(() => setSaveState((s) => { const n = { ...s }; delete n[rowId]; return n; }), 2000);
    } catch (err) {
      setSaveState((s) => ({ ...s, [rowId]: 'error' }));
      alert(`Save failed: ${err?.response?.data?.detail || err.message}`);
    }
  }, [metricKeys]);

  const handleDeleteRow = useCallback(async (periodEndDate) => {
    try {
      await researchService.deleteEarnings(companyId, periodEndDate);
      setDeleteConfirm(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      alert(`Delete failed: ${err?.response?.data?.detail || err.message}`);
      setDeleteConfirm(null);
    }
  }, [companyId, onRefresh]);

  const handleNormalize = useCallback(async () => {
    if (!window.confirm('Normalize all earnings rows for this company?\n\nThis will multiply stored values by their reporting_units factor (millions → ×1,000,000, etc.) and set reporting_units to "ones" in both DynamoDB and Redshift. This cannot be undone.')) return;
    setNormalizing(true);
    try {
      const result = await researchService.normalizeEarnings(companyId);
      if (onRefresh) onRefresh();
      alert(`Normalized ${result.total} row(s): ${result.scaled} scaled to actual values, ${result.stamped} already in ones (unit label updated).`);
    } catch (err) {
      alert(`Normalize failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setNormalizing(false);
    }
  }, [companyId, onRefresh]);

  // All toggle-able columns (excludes the 2 pinned identity columns)

  const DISPLAY_UNITS = [
    { value: 'ones',      label: 'Ones',      divisor: 1 },
    { value: 'thousands', label: 'Thousands', divisor: 1_000 },
    { value: 'millions',  label: 'Millions',  divisor: 1_000_000 },
    { value: 'billions',  label: 'Billions',  divisor: 1_000_000_000 },
  ];
  const displayDivisor = DISPLAY_UNITS.find((u) => u.value === displayUnits)?.divisor ?? 1;
  const displayUnitsLabel = DISPLAY_UNITS.find((u) => u.value === displayUnits)?.label ?? 'Ones';
  displayDivisorRef.current = displayDivisor;
  displayUnitsLabelRef.current = displayUnitsLabel;

  // Fields whose values must never be divided by the display unit —
  // mirrors the backend _NO_SCALE_METRICS set.
  const isNoScaleField = useCallback((field) => {
    const key = String(field || '').toLowerCase();
    return NO_SCALE_DISPLAY_FIELDS.has(key)
      || key.includes('pct')
      || key.includes('percent')
      || key.includes('margin')
      || key.includes('ratio')
      || key.includes('per_share');
  }, []);

  // Refresh all metric cells whenever the display unit changes
  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [displayUnits]);
  const toggleableCols = useMemo(() => [
    { field: 'period_end_date', label: 'Period End' },
    { field: 'currency',        label: 'Currency' },
    { field: 'reporting_units', label: 'Units' },
    ...metricKeys.map((k) => ({ field: k, label: k })),
    ...customMetricKeys.map((k) => ({ field: k, label: `${k} (custom)` })),
    { field: 'source_filename', label: 'Source File' },
  ], [metricKeys, customMetricKeys]);

  const toggleCol = useCallback((field) => {
    const api = gridRef.current?.api;
    if (!api) return;
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
        api.applyColumnState({ state: [{ colId: field, hide: false }] });
      } else {
        next.add(field);
        api.applyColumnState({ state: [{ colId: field, hide: true }] });
      }
      return next;
    });
  }, []);

  const fixedCols = [
    {
      field: 'fiscal_year',
      headerName: 'FY',
      pinned: 'left',
      lockPinned: true,
      lockPosition: true,
      width: 75,
      editable: false,
      filter: 'agNumberColumnFilter',
    },
    {
      field: 'quarter',
      headerName: 'Quarter',
      pinned: 'left',
      lockPinned: true,
      lockPosition: true,
      width: 100,
      editable: false,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'period_end_date',
      headerName: 'Period End',
      width: 120,
      editable: false,
      filter: 'agDateColumnFilter',
    },
    {
      field: 'currency',
      headerName: 'Currency',
      width: 90,
      editable: true,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'reporting_units',
      headerName: 'Units',
      width: 100,
      editable: false,
      filter: 'agTextColumnFilter',
      valueFormatter: () => displayUnitsLabelRef.current,
    },
  ];

  const metricCols = useMemo(() => {
    const makeFormatter = (k) => (p) => {
      if (p.value === null || p.value === undefined || p.value === '') return '';
      const n = Number(p.value);
      if (isNaN(n)) return String(p.value);
      const noScale = isNoScaleField(k);
      const div = noScale ? 1 : displayDivisorRef.current;
      const scaled = div !== 1 ? n / div : n;
      const display = scaled === 0 ? 0 : scaled;
      const forceScaledDecimals = !noScale && div > 1;
      const minDecimals = forceScaledDecimals ? 2 : 0;
      const maxDecimals = noScale ? 4 : 2;
      return display.toLocaleString(undefined, { minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals });
    };

    const scalar = metricKeys.map((k) => ({
      field: k,
      headerName: k,
      width: 130,
      editable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: makeFormatter(k),
      cellStyle: { textAlign: 'right' },
    }));

    const custom = customMetricKeys.map((k) => ({
      field: k,
      headerName: k,
      headerTooltip: 'Custom metric (read-only)',
      width: 130,
      editable: false,
      filter: 'agNumberColumnFilter',
      valueFormatter: makeFormatter(k),
      cellStyle: { textAlign: 'right', color: '#6c757d' },
    }));

    return [...scalar, ...custom];
  }, [metricKeys, customMetricKeys, isNoScaleField]);

  const sourceCols = [
    {
      field: 'source_filename',
      headerName: 'Source File',
      width: 180,
      editable: false,
      filter: 'agTextColumnFilter',
    },
    {
      field: '_actions',
      headerName: '',
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      pinned: 'right',
      lockPinned: true,
      sortable: false,
      filter: false,
      editable: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      cellStyle: { padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'visible' },
      cellRenderer: (params) => {
        const period = params.data?._period_end_date;
        if (!period) return null;
        if (deleteConfirm === period) {
          return (
            <div className="d-flex gap-1 align-items-center h-100">
              <button
                className="btn btn-danger btn-sm py-0 px-1"
                style={{ fontSize: '0.7rem' }}
                onClick={() => handleDeleteRow(period)}
              >
                Yes
              </button>
              <button
                className="btn btn-secondary btn-sm py-0 px-1"
                style={{ fontSize: '0.7rem' }}
                onClick={() => setDeleteConfirm(null)}
              >
                No
              </button>
            </div>
          );
        }
        return (
          <button
            className="btn btn-outline-danger btn-sm py-0 px-1"
            style={{ fontSize: '0.7rem' }}
            title="Delete row"
            onClick={() => setDeleteConfirm(period)}
          >
            <i className="bi bi-trash" />
          </button>
        );
      },
    },
  ];

  const columnDefs = [...fixedCols, ...metricCols, ...sourceCols];

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    movable: true,
    suppressMovableColumns: false,
  }), []);

  if (!rows || rows.length === 0) {
    return (
      <div className="text-muted small p-2">
        No structured earnings yet. Upload an Earnings file.
      </div>
    );
  }

  const savingCount = Object.values(saveState).filter((v) => v === 'saving').length;

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <span className="small text-muted">
          Click any metric cell to edit. Changes are saved automatically.
        </span>
        {savingCount > 0 && (
          <span className="badge bg-warning text-dark">
            <span className="spinner-border spinner-border-sm me-1" />Saving…
          </span>
        )}
        {Object.values(saveState).some((v) => v === 'saved') && (
          <span className="badge bg-success">Saved</span>
        )}

        {/* Column visibility picker */}
        <div className="position-relative ms-auto col-picker-dropdown">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setColPickerOpen((o) => !o)}
          >
            <i className="bi bi-layout-three-columns me-1" />Columns
          </button>
          {colPickerOpen && (
            <div
              className="border rounded bg-white shadow-sm p-2"
              style={{
                position: 'absolute',
                right: 0,
                top: '110%',
                zIndex: 1000,
                minWidth: 200,
                maxHeight: 320,
                overflowY: 'auto',
              }}
            >
              {toggleableCols.map(({ field, label }) => (
                <div key={field} className="form-check form-check-sm py-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`col-toggle-${field}`}
                    checked={!hiddenCols.has(field)}
                    onChange={() => toggleCol(field)}
                  />
                  <label className="form-check-label small" htmlFor={`col-toggle-${field}`}>
                    {label}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="d-flex align-items-center gap-1">
          <label className="small text-muted mb-0" htmlFor="earnings-grid-scale-select">Scale:</label>
          <select
            id="earnings-grid-scale-select"
            className="form-select form-select-sm"
            style={{ width: 'auto', minWidth: 130 }}
            value={displayUnits}
            onChange={(e) => setDisplayUnits(e.target.value)}
          >
            {DISPLAY_UNITS.map((u) => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        </div>

        <button className="btn btn-sm btn-outline-secondary" onClick={onRefresh}>
          <i className="bi bi-arrow-clockwise me-1" />Refresh
        </button>
      </div>
      <div style={{ height: 420, width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          theme={theme}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          stopEditingWhenCellsLoseFocus
          onCellValueChanged={onCellValueChanged}
          suppressColumnVirtualisation={false}
          rowDragManaged={false}
        />
      </div>
    </div>
  );
}
